export class WelcomeManager {
  static STORAGE_KEY = "rsu_welcome_data";
  static NEW_REGISTRATION_KEY = "rsu_new_registrations";
  static NEW_USER_DAYS = 7;

  /**
   * Get user key for storage operations
   */
  static getUserKey(user) {
    return user?.id || user?.email;
  }

  /**
   * Safe localStorage operations with error handling
   */
  static getStorageData(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || "{}");
    } catch (error) {
      return {};
    }
  }

  static setStorageData(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Mark user as newly registered (called after registration)
   */
  static markAsNewlyRegistered(user) {
    if (!user) return false;

    const userKey = this.getUserKey(user);
    const existingData = this.getStorageData(this.NEW_REGISTRATION_KEY);

    existingData[userKey] = {
      registeredAt: new Date().toISOString(),
      userId: userKey,
      email: user.email,
      shouldShowWelcome: true,
    };

    return this.setStorageData(this.NEW_REGISTRATION_KEY, existingData);
  }

  /**
   * Check if user should see welcome after login
   */
  static shouldShowWelcomeAfterLogin(user) {
    if (!user) return false;
    return (
      this.isRecentlyRegistered(user) ||
      (this.isNewUser(user) && !this.hasSeenWelcome(user))
    );
  }

  /**
   * Check if user registered recently
   */
  static isRecentlyRegistered(user) {
    if (!user) return false;

    const userKey = this.getUserKey(user);
    const registrationData = this.getStorageData(this.NEW_REGISTRATION_KEY);
    const userRegistration = registrationData[userKey];

    if (!userRegistration) return false;

    const registeredDate = new Date(userRegistration.registeredAt);
    const daysSinceRegistration =
      (Date.now() - registeredDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceRegistration <= 7 && userRegistration.shouldShowWelcome;
  }

  /**
   * Set flag to show welcome modal (called after successful login)
   */
  static setShowWelcomeFlag(user) {
    if (!user) return false;

    try {
      sessionStorage.setItem("show_welcome_modal", "true");
      sessionStorage.setItem("welcome_user_id", this.getUserKey(user));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if welcome modal should be shown on dashboard
   */
  static shouldShowWelcomeOnDashboard(user) {
    if (!user) return false;

    try {
      const showFlag = sessionStorage.getItem("show_welcome_modal");
      const flagUserId = sessionStorage.getItem("welcome_user_id");
      const currentUserId = this.getUserKey(user);

      if (showFlag === "true" && flagUserId === currentUserId) {
        return true;
      }

      return this.shouldShowWelcomeAfterLogin(user);
    } catch (error) {
      return false;
    }
  }

  /**
   * Mark welcome as seen and clear flags
   */
  static markWelcomeAsSeen(user) {
    if (!user) return false;

    const userKey = this.getUserKey(user);

    // Mark in main welcome data
    const existingData = this.getStorageData(this.STORAGE_KEY);
    existingData[userKey] = {
      hasSeenWelcome: true,
      seenAt: new Date().toISOString(),
      userId: userKey,
    };

    if (!this.setStorageData(this.STORAGE_KEY, existingData)) {
      return false;
    }

    // Clear registration flag
    const registrationData = this.getStorageData(this.NEW_REGISTRATION_KEY);
    if (registrationData[userKey]) {
      registrationData[userKey].shouldShowWelcome = false;
      this.setStorageData(this.NEW_REGISTRATION_KEY, registrationData);
    }

    // Clear session flags
    try {
      sessionStorage.removeItem("show_welcome_modal");
      sessionStorage.removeItem("welcome_user_id");
    } catch (error) {
      // Silent error for session storage
    }

    return true;
  }

  /**
   * Check if user has seen welcome modal
   */
  static hasSeenWelcome(user) {
    if (!user) return true;

    const userKey = this.getUserKey(user);
    const data = this.getStorageData(this.STORAGE_KEY);
    return data[userKey]?.hasSeenWelcome || false;
  }

  /**
   * Check if user is considered new (based on account age, etc.)
   */
  static isNewUser(user) {
    if (!user) return false;

    // Check account creation date
    const createdAt = user.created_at || user.createdAt;
    if (createdAt) {
      const createdDate = new Date(createdAt);
      const daysSinceCreation =
        (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceCreation <= this.NEW_USER_DAYS;
    }

    // Check login count
    if (typeof user.login_count === "number") {
      return user.login_count <= 3;
    }

    return true;
  }

  /**
   * Clean up old data (call periodically)
   */
  static cleanup() {
    const registrationData = this.getStorageData(this.NEW_REGISTRATION_KEY);
    const cleanedData = {};

    Object.entries(registrationData).forEach(([userKey, userData]) => {
      const registeredDate = new Date(userData.registeredAt);
      const daysSinceRegistration =
        (Date.now() - registeredDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceRegistration <= 30) {
        cleanedData[userKey] = userData;
      }
    });

    this.setStorageData(this.NEW_REGISTRATION_KEY, cleanedData);
  }

  /**
   * Reset welcome status for testing
   */
  static resetWelcomeStatus(user) {
    if (!user) return false;

    const userKey = this.getUserKey(user);

    // Clear from main storage
    const existingData = this.getStorageData(this.STORAGE_KEY);
    delete existingData[userKey];
    this.setStorageData(this.STORAGE_KEY, existingData);

    // Clear from registration storage
    const registrationData = this.getStorageData(this.NEW_REGISTRATION_KEY);
    delete registrationData[userKey];
    this.setStorageData(this.NEW_REGISTRATION_KEY, registrationData);

    // Clear session storage
    try {
      sessionStorage.removeItem("show_welcome_modal");
      sessionStorage.removeItem("welcome_user_id");
    } catch (error) {
      return false;
    }

    return true;
  }
}

export default WelcomeManager;
